import { getImageAdmission } from './generation-capacity.js';

function reserveImage(capacity) {
  return { ...capacity, admission: capacity.admission.map(entry =>
    ['account_media', 'account_all'].includes(entry.scope) ? { ...entry, used: entry.used + 1 } : entry) };
}

/** The approved manifest stays immutable. Capacity is refreshed at admission
 * waves and local completion events; this never polls a project's status. */
export function createImageQueue({ request, readCapacity, readBalance, assertCurrent,
  startSlot, cancelProject, shouldPauseOnOutcome = () => false,
  onUpdate, onPause, onResult, onCancelConfirmed, onCancelFailure }) {
  let pending = [...request.slots];
  const active = new Map();
  const outcomes = [];
  let stopped = false;
  let canceling = false;
  let cancelPromise = null;
  let capacity = null;
  let blocked = null;
  let fatal = null;
  let submittedCount = 0;
  let pausedCount = 0;
  let status = 'preparing';

  const update = () => onUpdate?.({ status, canceling, stopped,
    pending: pending.length, submitted: submittedCount, active: active.size,
    paused: pausedCount, completed: outcomes.length, capacity, blocked });

  function pausePending(reason) {
    blocked = reason;
    for (const slot of pending) onPause?.(slot, reason);
    pausedCount += pending.length;
    pending = [];
    update();
  }

  async function cancelEntry(entry, attemptedProjectIds) {
    try {
      const project = await entry.operation.submitted;
      // A local timeout can remove this entry without verifying remote delivery.
      // Let the cancellation boundary distinguish it from a completed project.
      attemptedProjectIds.push(project.projectId);
      const result = await cancelProject(project.projectId, entry.slot);
      if (result.confirmed === true) {
        if (entry.outcome?.unverified === true) {
          onCancelConfirmed?.(entry.slot, { ...result, projectId: project.projectId });
        }
      } else if (result.state !== 'finished') onCancelFailure?.(entry.slot, result);
      return result;
    } catch (error) {
      if (active.has(entry.slot.id)) onCancelFailure?.(entry.slot, { confirmed: false, error });
      return { confirmed: false, error };
    }
  }

  async function cancel() {
    if (cancelPromise) return cancelPromise;
    stopped = true;
    canceling = true;
    status = 'canceling';
    pausePending({ code: 'SUBMISSIONS_STOPPED', message: 'Stopped before submission. Review these saved images to resume.' });
    const entries = [...active.values()];
    const attemptedProjectIds = [];
    cancelPromise = Promise.all(entries.map(entry => cancelEntry(entry, attemptedProjectIds))).then(results => ({
      confirmed: results.filter(result => result.confirmed === true).length,
      unconfirmed: results.filter(result => result.confirmed !== true && result.state !== 'finished').length,
      finished: results.filter(result => result.confirmed !== true && result.state === 'finished').length,
      attemptedProjectIds
    })).finally(() => {
      canceling = false;
      cancelPromise = null;
      status = active.size ? 'stopping' : 'stopped';
      update();
    });
    return cancelPromise;
  }

  async function run() {
    status = stopped ? 'stopped' : 'running';
    update();
    try {
      while (pending.length && !stopped) {
        assertCurrent();
        try { capacity = await readCapacity(); }
        catch (error) {
          if (['REQUEST_CHANGED', 'SESSION_IDENTITY_CHANGED', 'AUTH_REQUIRED'].includes(error.code)) throw error;
          blocked = { code: 'CAPACITY_UNAVAILABLE', message: 'Your account capacity could not be loaded. Refresh capacity before reviewing the remaining images.', cause: error };
          break;
        }
        if (stopped) break;
        assertCurrent();
        let budget = capacity;
        let admitted = 0;
        while (pending.length && !stopped) {
          const balance = readBalance();
          const index = pending.findIndex(slot => getImageAdmission(budget, slot, balance).canSubmit);
          if (index < 0) break;
          assertCurrent();
          const slot = pending[index];
          const operation = startSlot(slot);
          pending.splice(index, 1);
          const entry = { slot, operation };
          active.set(slot.id, entry);
          entry.done = operation.completed.then(outcome => {
            entry.outcome = outcome;
            active.delete(slot.id);
            outcomes.push({ slot, outcome });
            onResult?.(slot, outcome);
            if (shouldPauseOnOutcome(outcome)) {
              stopped = true;
              blocked = { code: outcome.errorCode, message: outcome.error, outcome };
            }
            update();
            return outcome;
          });
          try {
            await operation.submitted;
            submittedCount += 1;
            admitted += 1;
            budget = reserveImage(budget);
            capacity = budget;
            update();
          } catch (error) {
            fatal = error;
            stopped = true;
            blocked = { code: error.code, message: error.message };
          }
        }
        if (stopped || !pending.length) break;
        if (active.size) {
          status = 'waiting';
          update();
          await Promise.race([...active.values()].map(entry => entry.done));
          status = 'running';
        } else if (!admitted) {
          const admission = getImageAdmission(budget, pending[0], readBalance());
          blocked = { code: admission.status === 'full' ? 'ACCOUNT_QUEUE_FULL' : 'CAPACITY_UNAVAILABLE',
            message: admission.message };
          break;
        }
      }
    } catch (error) {
      fatal = error;
      stopped = true;
      blocked = { code: error.code, message: error.message };
    }
    if (pending.length) pausePending(blocked || { code: 'SUBMISSIONS_STOPPED', message: 'Stopped before submission.' });
    await Promise.all([...active.values()].map(entry => entry.done));
    status = stopped ? 'stopped' : blocked ? 'paused' : 'completed';
    update();
    if (fatal) throw fatal;
    return { status, outcomes, capacity, blocked };
  }

  return { run, cancel, get stopped() { return stopped; } };
}
